import { useState, useCallback } from 'react';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { requestService } from 'shared/utils/services/requestService';
import { useWantedEntries } from 'wants/hooks/useWantedEntries';
import { isWantProductEntry } from 'wants/utils/isWantEntry';
import { useStoreSelector } from 'store/hooks/storeHooks';

export const useWantedProduct = (productId: string | number) => {
  const [loading, setLoading] = useState(false);
  const { wantEntries, isValidating, error, mutate } = useWantedEntries();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);

  const wantEntry = wantEntries?.find(
    (entry) => isWantProductEntry(entry) && String(entry.productId) === String(productId),
  );

  const wantId = wantEntry?.id;
  const wanted = Boolean(wantId);

  const wantProduct = useCallback(async () => {
    if (!productId || !isAuthenticated) return;

    try {
      await requestService({
        url: '/web-api/v1/wants',
        method: 'POST',
        data: JSON.stringify({
          want: {
            productId,
          },
        }),
      });
      await mutate();
    } catch (_error) {
      if (typeof _error !== 'undefined') {
        bugsnag(_error, { context: 'Adding product want' });
      }
    }
  }, [productId, mutate, isAuthenticated]);

  const unwantProduct = useCallback(async () => {
    if (!wantId || !isAuthenticated) return;

    try {
      await requestService({
        url: `/web-api/v1/wants/${wantId}`,
        method: 'DELETE',
      });
      await mutate();
    } catch (_error) {
      if (typeof _error !== 'undefined') {
        bugsnag(_error, { context: 'Deleting product want' });
      }
    }
  }, [wantId, mutate, isAuthenticated]);

  const toggleWant = useCallback(async () => {
    if (loading) return;

    setLoading(true);

    if (wanted) {
      await unwantProduct();
    } else {
      await wantProduct();
    }

    setLoading(false);
  }, [loading, wanted, wantProduct, unwantProduct]);

  return {
    wanted,
    wantId,
    productId,
    isLoading: loading || (!wantEntries && isValidating),
    error,
    toggleWant,
  };
};
